import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { WeaveCopilotV1 } from '@frontend/api-weave-copilot';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, useAlert } from '@frontend/design-system';
import { useChatbotAssistant } from '../../provider/chatbot-assistant.provider';
import { getWeaveCopilotTrackingId, WEAVE_COPILOT_ACTIONS } from '../../tracking-ids';

export const MessageFeedback: FC<{ messageId: string }> = ({ messageId }) => {
  const alerts = useAlert();
  const { t } = useTranslation('aiOnboarding');
  const { locationId, updateFeedback, messages, source } = useChatbotAssistant();
  const currentFeedback = useMemo(() => messages.find((msg) => msg.id === messageId)?.feedback, [messages, messageId]);

  const { mutate: submitFeedback, isLoading: isMutating } = WeaveCopilotV1.Mutations.useUpsertFeedbackMutation({
    options: {
      onSuccess: (_, request) => {
        updateFeedback(messageId, request.rating === 'positive' ? 'positive' : 'negative');
        alerts.success(t('Thank you for your feedback!'));
      },
      onError: () => {
        alerts.error(t('Something went wrong. Please try again.'));
      },
    },
  });

  return (
    <div css={styles.feedbackBar}>
      <Button
        iconName='feedback-bad-small'
        size='small'
        variant='secondary'
        aria-label='Not Helpful'
        disabled={isMutating}
        trackingId={getWeaveCopilotTrackingId({ source, action: WEAVE_COPILOT_ACTIONS.feedbackNegative })}
        onClick={() =>
          submitFeedback({
            locationId,
            sessionId: messageId,
            rating: 'negative',
          })
        }
        css={currentFeedback === 'negative' ? styles.negativeIcon : undefined}
      />
      <Button
        iconName='feedback-small'
        size='small'
        variant='secondary'
        aria-label='Helpful'
        disabled={isMutating}
        trackingId={getWeaveCopilotTrackingId({ source, action: WEAVE_COPILOT_ACTIONS.feedbackPositive })}
        onClick={() =>
          submitFeedback({
            locationId,
            sessionId: messageId,
            rating: 'positive',
          })
        }
        css={currentFeedback === 'positive' ? styles.positiveIcon : undefined}
      />
    </div>
  );
};

const styles = {
  feedbackBar: css`
    display: flex;
    margin-top: ${theme.spacing(0.5)};
    align-self: flex-end;
  `,
  positiveIcon: css`
    background-color: ${theme.colors.success10};
  `,
  negativeIcon: css`
    background-color: ${theme.colors.critical10};
  `,
};
